import type jsPDF from 'jspdf';
import { buildCompanyForPdf } from '../../services/pdf/companyForPdf';
import {
    generatePlanungsfragenPdf,
    type PlanungsfragenPdfOptionen,
} from '../../services/pdf/generatorPlanungsfragen';
import type { Planungsfrage } from './types';

/**
 * Anschluss an werfende Nachbarn (jsPDF, Briefkopfaufbereitung).
 *
 * Muster wie `sige-einweisung/einweisungIntegration.ts`: die Komponente soll
 * nicht in einem `try` stehen, und ein kaputtes Logo im Firmenprofil darf
 * nicht das ganze Modul weissbrennen.
 *
 * Der Diskriminator ist bewusst eine ZEICHENKETTE und kein boolean: das
 * Projekt uebersetzt ohne `strictNullChecks`, und ein `{ ok: false }` liesse
 * sich dort versehentlich auf einem Objekt lesen, das gar keinen `ok`-
 * Schluessel hat.
 */
export type Anschluss<T> =
    | { status: 'ok'; wert: T }
    | { status: 'fehler'; fehler: string };

const alsFehler = (e: unknown, was: string): { status: 'fehler'; fehler: string } => ({
    status: 'fehler',
    fehler: `${was}: ${e instanceof Error ? e.message : String(e)}`,
});

export interface PdfKontext {
    /** Rohe Firmenstammdaten aus dem Profil. */
    company: unknown;
    quoteConfig: unknown;
    invoiceConfig: unknown;
}

/**
 * Erzeugt die Klaerungsliste.
 *
 * `buildCompanyForPdf` ist PFLICHT — rohe Stammdaten durchzureichen ergibt
 * ein Blatt ohne Briefpapier, und das faellt erst beim Empfaenger auf.
 */
export const baueKlaerungslistePdf = (
    fragen: Planungsfrage[],
    kontext: PdfKontext,
    optionen: PlanungsfragenPdfOptionen,
): Anschluss<jsPDF> => {
    try {
        const company = buildCompanyForPdf(
            kontext.company as never,
            kontext.quoteConfig as never,
            kontext.invoiceConfig as never,
        );
        return { status: 'ok', wert: generatePlanungsfragenPdf(fragen, company, optionen) };
    } catch (e) {
        return alsFehler(e, 'Das PDF konnte nicht erzeugt werden');
    }
};

/** Speichert das erzeugte Dokument. Getrennt vom Erzeugen, damit der
 *  Aufrufer dasselbe Blatt auch anzeigen oder verschicken kann. */
export const speicherePdf = (doc: jsPDF, dateiname: string): Anschluss<true> => {
    try {
        doc.save(dateiname);
        return { status: 'ok', wert: true };
    } catch (e) {
        return alsFehler(e, 'Das PDF konnte nicht gespeichert werden');
    }
};

/** Bytes fuer die Ablage im Dokumentenmanagement bzw. den Versand. */
export const pdfAlsBlob = (doc: jsPDF): Anschluss<Blob> => {
    try {
        return { status: 'ok', wert: doc.output('blob') as Blob };
    } catch (e) {
        return alsFehler(e, 'Das PDF konnte nicht ausgegeben werden');
    }
};

/**
 * Dateiname der Klaerungsliste.
 *
 * Ohne Sonderzeichen und ohne Leerzeichen: der Name landet auch als
 * Objektname im Storage-Bucket, und dort ist ein Schraegstrich aus einem
 * Projektnamen ein neuer Ordner.
 */
export const klaerungslisteDateiname = (
    projektName: string,
    empfaenger?: string,
    datumIso?: string,
): string => {
    const sauber = (s: string) => (s || '')
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const teile = ['Klaerungsliste', sauber(projektName)];
    if (empfaenger) teile.push(sauber(empfaenger));
    teile.push((datumIso || '').slice(0, 10) || 'ohne-Datum');
    return `${teile.filter(Boolean).join('_')}.pdf`;
};

/** ISO-Tag → TT.MM.JJJJ durch Zerlegen; leer bei unbrauchbarer Eingabe. */
const tagDeutsch = (iso: string | null | undefined): string => {
    const t = String(iso || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return '';
    const [j, m, d] = t.split('-');
    return `${d}.${m}.${j}`;
};

/**
 * Anzeigename der versendeten Fassung in der Ablage:
 * „Klärungsliste an Bauherr / Bauherrenvertreter (Müller) — 17.10.2026".
 *
 * Adressat UND Person, weil beides zaehlt: die Rolle sagt, welche Fragen
 * auf dem Blatt stehen, der Name, wem es zuging. Ohne Namen bleibt die
 * Rolle allein stehen.
 */
export const klaerungslisteAblageTitel = (
    adressatLabel: string,
    empfaengerName: string | null | undefined,
    datumIso: string,
): string => {
    const rolle = (adressatLabel || '').trim() || 'Adressat';
    const name = (empfaengerName || '').replace(/\s+/g, ' ').trim();
    const wer = name ? `${rolle} (${name})` : rolle;
    const tag = tagDeutsch(datumIso);
    return tag ? `Klärungsliste an ${wer} — ${tag}` : `Klärungsliste an ${wer}`;
};

/**
 * Stabile Kennung einer versendeten Fassung fuer den Doppelablage-Schutz
 * (`fassung_ref`): EIN geoeffneter Versanddialog ist EINE Fassung. Scheitert
 * der Versand und wird im selben Dialog erneut versucht, findet die Ablage
 * die Fassung wieder (`schon_abgelegt`) statt eine zweite Zeile anzulegen.
 * Jeder neu geoeffnete Dialog ist eine neue Fassung — ein Erinnerungs-
 * schreiben eine Woche spaeter ist ein eigenes Blatt.
 */
export const klaerungslisteFassungRef = (adressat: string, geoeffnetAmIso: string): string =>
    `klaerungsliste:${(adressat || '').trim() || 'alle'}:${(geoeffnetAmIso || '').trim() || 'ohne-Zeit'}`;
